// customerFilterSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ActivityFilter,
  SegmentFilter,
  JourneyTypeFilter,
  ConsumptionFilter,
  SegmentImportanceFilter,
  SegmentSizeFilter,
  ActionFilter,
  ContactFilter,
  InvoiceDeliveryFilter,
  DirectDebitFilter,
  DeliveryEndFilter,
  DeliveryEndReasonFilter,
} from '../../components/customer-list/components/FilterButton';

// Interface för ett sparat filter
export interface SavedFilter {
  id: string; // Unikt ID, t.ex., timestamp eller UUID
  name: string;
  filters: { // Exakt vilka filtervärden som sparades
    segmentFilter: SegmentFilter;
    segmentImportanceFilter: SegmentImportanceFilter;
    segmentSizeFilter: SegmentSizeFilter;
    activityFilter: ActivityFilter;
    journeyTypeFilter: JourneyTypeFilter;
    consumptionFilter: ConsumptionFilter;
    searchQuery: string;
    actionFilter: ActionFilter;
    contactFilter: ContactFilter;
    invoiceDeliveryFilter: InvoiceDeliveryFilter;
    directDebitFilter: DirectDebitFilter;
    deliveryEndFilter?: DeliveryEndFilter; // Valfri för bakåtkompatibilitet med redan sparade filter
    deliveryEndReasonFilter?: DeliveryEndReasonFilter; // Valfri för bakåtkompatibilitet med redan sparade filter
  };
}

// Helper function to load filters from localStorage
const loadSavedFiltersFromStorage = (): SavedFilter[] => {
  try {
    const savedFiltersJson = localStorage.getItem('userSavedCustomerFilters');
    return savedFiltersJson ? JSON.parse(savedFiltersJson) : [];
  } catch (error) {
    console.error("Could not load saved filters from localStorage:", error);
    return [];
  }
};

// Helper function to save filters to localStorage
const saveFiltersToStorage = (filters: SavedFilter[]) => {
  try {
    localStorage.setItem('userSavedCustomerFilters', JSON.stringify(filters));
  } catch (error) {
    console.error("Could not save filters to localStorage:", error);
  }
};

// Slice state
interface CustomerFilterState {
  segmentFilter: SegmentFilter;
  segmentImportanceFilter: SegmentImportanceFilter;
  segmentSizeFilter: SegmentSizeFilter;
  activityFilter: ActivityFilter; // "Inloggning"
  journeyTypeFilter: JourneyTypeFilter;
  consumptionFilter: ConsumptionFilter;
  actionFilter: ActionFilter;
  contactFilter: ContactFilter;
  invoiceDeliveryFilter: InvoiceDeliveryFilter;
  directDebitFilter: DirectDebitFilter;
  deliveryEndFilter: DeliveryEndFilter;
  deliveryEndReasonFilter: DeliveryEndReasonFilter;
  searchQuery: string;
  activePredefinedFilter: string | null;
  activeSavedFilterId: string | null;
  showFilters: boolean;
  savedFilters: SavedFilter[];
  // Pagination state
  currentPage: number;
  rowsPerPage: number;
}

// Initial state
const initialState: CustomerFilterState = {
  segmentFilter: null,
  segmentImportanceFilter: null,
  segmentSizeFilter: null,
  activityFilter: 'all',
  journeyTypeFilter: null,
  consumptionFilter: 'all',
  actionFilter: 'all',
  contactFilter: 'all',
  invoiceDeliveryFilter: null,
  directDebitFilter: 'all',
  deliveryEndFilter: null,
  deliveryEndReasonFilter: null,
  searchQuery: '',
  activePredefinedFilter: null,
  activeSavedFilterId: null,
  showFilters: false,
  savedFilters: loadSavedFiltersFromStorage(),
  // Pagination initial state
  currentPage: 0,
  rowsPerPage: 10,
};

// Interface för predefined filter payload
interface ApplyPredefinedFilterPayload {
  filterId: string;
  filters: Partial<Omit<CustomerFilterState, 'savedFilters' | 'showFilters' | 'activeSavedFilterId' | 'activePredefinedFilter'>>;
}

// Interface för apply saved filter payload
interface ApplySavedFilterPayload {
  filterId: string;
  filters: SavedFilter['filters']; // Använd SavedFilter['filters'] typen
}

// Create slice med explicit typning
const customerFilterSlice = createSlice({
  name: 'customerFilter',
  initialState,
  reducers: {
    // --- Existing Reducers ---
    setSegmentFilter(state, action: PayloadAction<SegmentFilter>) {
      state.segmentFilter = action.payload;
      state.activePredefinedFilter = null; // Clear active filters on manual change
      state.activeSavedFilterId = null;
    },
    setSegmentImportanceFilter(state, action: PayloadAction<SegmentImportanceFilter>) {
      state.segmentImportanceFilter = action.payload;
      state.activePredefinedFilter = null;
      state.activeSavedFilterId = null;
    },
    setSegmentSizeFilter(state, action: PayloadAction<SegmentSizeFilter>) {
      state.segmentSizeFilter = action.payload;
      state.activePredefinedFilter = null;
      state.activeSavedFilterId = null;
    },
    setActivityFilter(state, action: PayloadAction<ActivityFilter>) {
      state.activityFilter = action.payload;
      state.activePredefinedFilter = null;
      state.activeSavedFilterId = null;
    },
    setJourneyTypeFilter(state, action: PayloadAction<JourneyTypeFilter>) {
      state.journeyTypeFilter = action.payload;
      state.activePredefinedFilter = null;
      state.activeSavedFilterId = null;
    },
    setConsumptionFilter(state, action: PayloadAction<ConsumptionFilter>) {
      state.consumptionFilter = action.payload;
      state.activePredefinedFilter = null;
      state.activeSavedFilterId = null;
    },
    setActionFilter(state, action: PayloadAction<ActionFilter>) {
      state.actionFilter = action.payload;
      state.activePredefinedFilter = null; // Rensa andra aktiva filter
      state.activeSavedFilterId = null;
    },
    setContactFilter(state, action: PayloadAction<ContactFilter>) {
      state.contactFilter = action.payload;
      state.activePredefinedFilter = null;
      state.activeSavedFilterId = null;
    },
    setInvoiceDeliveryFilter(state, action: PayloadAction<InvoiceDeliveryFilter>) {
      state.invoiceDeliveryFilter = action.payload;
      state.activePredefinedFilter = null;
      state.activeSavedFilterId = null;
    },
    setDirectDebitFilter(state, action: PayloadAction<DirectDebitFilter>) {
      state.directDebitFilter = action.payload;
      state.activePredefinedFilter = null;
      state.activeSavedFilterId = null;
    },
    setDeliveryEndFilter(state, action: PayloadAction<DeliveryEndFilter>) {
      state.deliveryEndFilter = action.payload;
      state.activePredefinedFilter = null;
      state.activeSavedFilterId = null;
    },
    setDeliveryEndReasonFilter(state, action: PayloadAction<DeliveryEndReasonFilter>) {
      state.deliveryEndReasonFilter = action.payload;
      state.activePredefinedFilter = null;
      state.activeSavedFilterId = null;
    },
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
      state.activePredefinedFilter = null;
      state.activeSavedFilterId = null;
    },
    setActivePredefinedFilter(state, action: PayloadAction<string | null>) {
      state.activePredefinedFilter = action.payload;
      state.activeSavedFilterId = null; // Clear saved filter if predefined is set
    },
    setShowFilters(state, action: PayloadAction<boolean>) {
      state.showFilters = action.payload;
    },
    clearAllFilters(state) {
       // Return initial state but keep savedFilters, showFilters and pagination
      const currentSavedFilters = state.savedFilters;
      const currentShowFilters = state.showFilters;
      const currentPage = state.currentPage;
      const currentRowsPerPage = state.rowsPerPage;
      return {
        ...initialState,
        savedFilters: currentSavedFilters, // Keep saved filters
        showFilters: currentShowFilters, // Keep showFilters value
        currentPage: currentPage, // Keep current page
        rowsPerPage: currentRowsPerPage // Keep rows per page
      };
    },
    applyPredefinedFilter(state, action: PayloadAction<ApplyPredefinedFilterPayload>) {
      const { filterId, filters } = action.payload;

      // Om samma filter redan är aktivt, rensa
      if (state.activePredefinedFilter === filterId) {
        const currentSavedFilters = state.savedFilters;
        const currentShowFilters = state.showFilters;
        const currentPage = state.currentPage;
        const currentRowsPerPage = state.rowsPerPage;
        return {
          ...initialState,
          savedFilters: currentSavedFilters,
          showFilters: currentShowFilters,
          currentPage: currentPage,
          rowsPerPage: currentRowsPerPage
        };
      }

      // Apply filters
      state.segmentFilter = filters.segmentFilter !== undefined ? filters.segmentFilter : initialState.segmentFilter;
      state.segmentImportanceFilter = filters.segmentImportanceFilter !== undefined ? filters.segmentImportanceFilter : initialState.segmentImportanceFilter;
      state.segmentSizeFilter = filters.segmentSizeFilter !== undefined ? filters.segmentSizeFilter : initialState.segmentSizeFilter;
      state.activityFilter = filters.activityFilter !== undefined ? filters.activityFilter : initialState.activityFilter;
      state.journeyTypeFilter = filters.journeyTypeFilter !== undefined ? filters.journeyTypeFilter : initialState.journeyTypeFilter;
      state.consumptionFilter = filters.consumptionFilter !== undefined ? filters.consumptionFilter : initialState.consumptionFilter;
      state.actionFilter = filters.actionFilter !== undefined ? filters.actionFilter : initialState.actionFilter;
      state.contactFilter = filters.contactFilter !== undefined ? filters.contactFilter : initialState.contactFilter;
      state.invoiceDeliveryFilter = filters.invoiceDeliveryFilter !== undefined ? filters.invoiceDeliveryFilter : initialState.invoiceDeliveryFilter;
      state.directDebitFilter = filters.directDebitFilter !== undefined ? filters.directDebitFilter : initialState.directDebitFilter;
      state.deliveryEndFilter = filters.deliveryEndFilter !== undefined ? filters.deliveryEndFilter : initialState.deliveryEndFilter;
      state.deliveryEndReasonFilter = filters.deliveryEndReasonFilter !== undefined ? filters.deliveryEndReasonFilter : initialState.deliveryEndReasonFilter;
      state.searchQuery = filters.searchQuery !== undefined ? filters.searchQuery : initialState.searchQuery;

      // Set active filter and clear the other type
      state.activePredefinedFilter = filterId;
      state.activeSavedFilterId = null;
    },

    // --- NEW Reducers for Saved Filters ---
    addSavedFilter(state, action: PayloadAction<SavedFilter>) {
      // Optional: Check for duplicate names or IDs before adding
      state.savedFilters.push(action.payload);
      saveFiltersToStorage(state.savedFilters); // Persist changes
    },
    removeSavedFilter(state, action: PayloadAction<{ id: string }>) {
      state.savedFilters = state.savedFilters.filter(filter => filter.id !== action.payload.id);
      saveFiltersToStorage(state.savedFilters); // Persist changes
      // If the removed filter was active, clear active state
      if (state.activeSavedFilterId === action.payload.id) {
        state.activeSavedFilterId = null;
      }
    },
    applySavedFilter(state, action: PayloadAction<ApplySavedFilterPayload>) {
        const { filterId, filters } = action.payload;
        
        // Om samma sparade filter redan är aktivt, rensa
        if (state.activeSavedFilterId === filterId) {
          const currentSavedFilters = state.savedFilters;
          const currentShowFilters = state.showFilters;
          const currentPage = state.currentPage;
          const currentRowsPerPage = state.rowsPerPage;
          return {
            ...initialState,
            savedFilters: currentSavedFilters,
            showFilters: currentShowFilters,
            currentPage: currentPage,
            rowsPerPage: currentRowsPerPage
          };
        }

        // Apply all filters from the saved filter object
        state.segmentFilter = filters.segmentFilter;
        state.segmentImportanceFilter = filters.segmentImportanceFilter;
        state.segmentSizeFilter = filters.segmentSizeFilter;
        state.activityFilter = filters.activityFilter;
        state.journeyTypeFilter = filters.journeyTypeFilter;
        state.consumptionFilter = filters.consumptionFilter;
        state.actionFilter = filters.actionFilter;
        state.contactFilter = filters.contactFilter;
        state.invoiceDeliveryFilter = filters.invoiceDeliveryFilter;
        state.directDebitFilter = filters.directDebitFilter;
        state.deliveryEndFilter = filters.deliveryEndFilter ?? null;
        state.deliveryEndReasonFilter = filters.deliveryEndReasonFilter ?? null;
        state.searchQuery = filters.searchQuery;


        state.activeSavedFilterId = filterId;
        state.activePredefinedFilter = null;
    },
    // Pagination actions
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },
    setRowsPerPage(state, action: PayloadAction<number>) {
      state.rowsPerPage = action.payload;
      state.currentPage = 0; // Reset to first page when changing rows per page
    },
    // Optional: Load filters explicitly if not done via initialState
    // loadSavedFilters(state, action: PayloadAction<SavedFilter[]>) {
    //   state.savedFilters = action.payload;
    // }
  }
});

// Export actions
export const {
  setSegmentFilter,
  setSegmentImportanceFilter,
  setSegmentSizeFilter,
  setActivityFilter,
  setJourneyTypeFilter,
  setConsumptionFilter,
  setSearchQuery,
  setActivePredefinedFilter,
  setShowFilters,
  clearAllFilters,
  applyPredefinedFilter,
  addSavedFilter,
  removeSavedFilter,
  applySavedFilter,
  setActionFilter,
  setContactFilter,
  setInvoiceDeliveryFilter,
  setDirectDebitFilter,
  setDeliveryEndFilter,
  setDeliveryEndReasonFilter,
  setCurrentPage,
  setRowsPerPage,
} = customerFilterSlice.actions;

// Export reducer
export default customerFilterSlice.reducer;